import EntityManager from "@/components/EntityManager";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { inr } from "@/lib/format";
import { fmtDate, today } from "@/lib/format";
import { printExpenseRegister, printExpenseSummary } from "@/lib/print";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart3, Printer, TrendingUp, Paperclip, Eye, MessageCircle } from "lucide-react";
import { HEAD_OFFICE } from "@/lib/company";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

const CATEGORIES = [
  "Fuel",
  "Driver Salary",
  "Vehicle Maintenance",
  "Two Wheeler",
  "Two Wheeler Service",
  "Car",
  "Tolls & Parking",
  "Insurance",
  "Office & Admin",
  "Mess Expenses",
  "Stationary Expenses",
  "Gas Cylinder",
  "Loading/Unloading",
  "Other",
];
import { BRANCHES } from "@/lib/branches";
const BRANCH_COLORS = { Chennai: "#0f172a", Tada: "#10b981", Hyderabad: "#f59e0b", Jaipur: "#3b82f6" };

function ProofActions({ row }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/expenses/${row.id}/proof`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Bill photo attached");
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid={`proof-file-input-${row.id}`} />
      <Button
        variant="ghost"
        size="icon"
        className={`h-8 w-8 ${row.proof_path ? "text-emerald-600" : "text-slate-400"}`}
        title={row.proof_path ? "Replace bill photo" : "Attach bill photo"}
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        data-testid="attach-proof-btn"
      >
        <Paperclip className="h-4 w-4" />
      </Button>
    </>
  );
}

const PROOF_ALERT_LIMIT = 5000;

const proofUrl = (r) => `${API}/files/${r.proof_path}?auth=${localStorage.getItem("ntc_token")}`;

const expenseWhatsApp = (r) => {
  const lines = [
    `*NTC Expense — ${r.category || "Other"}*`,
    `Date: ${fmtDate(r.date)} | Branch: ${r.office || "Chennai"}`,
    r.description ? `Description: ${r.description}` : "",
    r.vehicle_no ? `Vehicle: ${r.vehicle_no}` : "",
    `Amount: ₹${Number(r.amount || 0).toLocaleString("en-IN")}`,
    r.proof_path ? "Bill proof: attached in system" : "",
    "",
    "— Nisha Transport Corporation",
  ].filter((l) => l !== "");
  window.open(`https://wa.me/${HEAD_OFFICE.whatsapp}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
};

function ExpenseTrend() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (open) api.get("/expenses").then((r) => setRows(r.data || [])).catch(() => {});
  }, [open]);
  const { data, activeBranches } = useMemo(() => {
    const months = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    }
    const byMonth = Object.fromEntries(months.map((m) => [m, {}]));
    const active = new Set();
    rows.forEach((r) => {
      const m = String(r.date || "").slice(0, 7);
      if (!byMonth[m]) return;
      const b = r.office || "Chennai";
      active.add(b);
      byMonth[m][b] = (byMonth[m][b] || 0) + (Number(r.amount) || 0);
    });
    const data = months.map((m) => ({
      month: new Date(`${m}-01`).toLocaleDateString("en-GB", { month: "short", year: "2-digit" }),
      ...byMonth[m],
    }));
    return { data, activeBranches: BRANCHES.filter((b) => active.has(b)) };
  }, [rows]);

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="expense-trend-btn">
        <TrendingUp className="mr-2 h-4 w-4" /> Trend
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[820px]">
          <DialogHeader><DialogTitle className="font-display">Expense Trend — Last 12 Months (Branch-wise)</DialogTitle></DialogHeader>
          {activeBranches.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground" data-testid="expense-trend-empty">No expenses in the last 12 months.</div>
          ) : (
            <div className="h-[360px] w-full" data-testid="expense-trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                  <Tooltip formatter={(v) => inr(v)} />
                  <Legend />
                  {activeBranches.map((b) => (
                    <Line key={b} type="monotone" dataKey={b} stroke={BRANCH_COLORS[b]} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function ExpenseSummary() {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(today().slice(0, 7));
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (open) api.get("/expenses").then((r) => setRows(r.data || [])).catch(() => {});
  }, [open]);
  const list = useMemo(() => {
    const m = {};
    rows.filter((r) => String(r.date || "").startsWith(month)).forEach((r) => {
      const b = r.office || "Chennai";
      const a = (m[b] = m[b] || { branch: b, count: 0, total: 0, cats: {} });
      a.count += 1;
      a.total += Number(r.amount) || 0;
      const c = r.category || "Other";
      const cat = (a.cats[c] = a.cats[c] || { category: c, count: 0, total: 0 });
      cat.count += 1;
      cat.total += Number(r.amount) || 0;
    });
    return Object.values(m)
      .map((a) => ({ ...a, categories: Object.values(a.cats).sort((x, y) => y.total - x.total) }))
      .sort((x, y) => y.total - x.total);
  }, [rows, month]);
  const grand = list.reduce((a, r) => a + r.total, 0);
  const monthLabel = new Date(`${month}-01`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="expense-summary-btn">
        <BarChart3 className="mr-2 h-4 w-4" /> Monthly Summary
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader><DialogTitle className="font-display">Monthly Expense Summary</DialogTitle></DialogHeader>
          <div className="flex items-end gap-3">
            <Input type="month" className="h-9 w-[180px]" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="expense-summary-month" />
            <Button variant="outline" className="h-9" disabled={!list.length} onClick={() => printExpenseSummary(monthLabel, list)} data-testid="expense-summary-print">
              <Printer className="mr-2 h-4 w-4" /> Print
            </Button>
          </div>
          <div className="overflow-hidden rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  {["Branch", "Expenses", "Total"].map((h, i) => (
                    <TableHead key={h} className={`text-[11px] uppercase tracking-[0.12em] text-slate-500 ${i > 0 ? "text-right" : ""}`}>{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.length === 0 && <TableRow><TableCell colSpan={3} className="py-8 text-center text-muted-foreground">No expenses for {monthLabel}.</TableCell></TableRow>}
                {list.map((r) => (
                  <React.Fragment key={r.branch}>
                    <TableRow data-testid="expense-summary-row">
                      <TableCell className="text-sm font-semibold">{r.branch}</TableCell>
                      <TableCell className="text-right text-sm font-semibold tabular-nums">{r.count}</TableCell>
                      <TableCell className="text-right text-sm font-semibold tabular-nums">{inr(r.total)}</TableCell>
                    </TableRow>
                    {r.categories.map((c) => (
                      <TableRow key={`${r.branch}-${c.category}`} className="bg-slate-50/50 hover:bg-slate-50/50" data-testid="expense-summary-cat-row">
                        <TableCell className="py-1.5 pl-8 text-xs text-slate-600">↳ {c.category}</TableCell>
                        <TableCell className="py-1.5 text-right text-xs tabular-nums text-slate-600">{c.count}</TableCell>
                        <TableCell className="py-1.5 text-right text-xs tabular-nums text-slate-600">{inr(c.total)}</TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
                {list.length > 0 && (
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableCell className="text-sm font-bold">TOTAL</TableCell>
                    <TableCell className="text-right text-sm font-bold tabular-nums">{list.reduce((a, r) => a + r.count, 0)}</TableCell>
                    <TableCell className="text-right text-sm font-bold tabular-nums">{inr(grand)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

const exportExpensesCsv = (list) => {
  const headers = ["Sl No.", "Date", "Branch", "Category", "Description", "Vehicle", "Amount"];
  const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const lines = [headers.join(",")];
  list.forEach((r, i) => lines.push([i + 1, r.date ? fmtDate(r.date) : "", r.office || "Chennai", r.category || "", r.description || "", r.vehicle_no || "", r.amount || 0].map(cell).join(",")));
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ntc-expenses-${today()}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export default function Expenses() {
  const [allRows, setAllRows] = useState([]);
  const [proofLimit, setProofLimit] = useState(PROOF_ALERT_LIMIT);
  useEffect(() => {
    api.get("/expenses").then((r) => setAllRows(r.data || [])).catch(() => {});
    api.get("/app-settings").then((r) => setProofLimit(Number(r.data?.proof_alert_limit) || PROOF_ALERT_LIMIT)).catch(() => {});
  }, []);
  const manualSuggestions = useMemo(() => {
    const used = new Set(allRows.map((r) => r.category).filter(Boolean));
    return [...used].filter((c) => !CATEGORIES.includes(c)).sort();
  }, [allRows]);

  const aiCategorize = async (form) => {
    const res = await api.post("/ai/categorize-expense", {
      description: form.description || "",
      amount: Number(form.amount) || 0,
    });
    return res.data.category;
  };

  return (
    <EntityManager
      title="NTC Expenses"
      subtitle="Track fuel, salaries, maintenance and more. Use AI to auto-categorize."
      endpoint="expenses"
      entityName="Expense"
      selectable
      transformBeforeSave={(f) => {
        const { category_manual, ...rest } = f;
        return category_manual && category_manual.trim() ? { ...rest, category: category_manual.trim() } : rest;
      }}
      onPrintSelected={(list) => printExpenseRegister(list)}
      exportCsv={exportExpensesCsv}
      headerExtra={() => (
        <div className="flex items-center gap-2">
          <ExpenseTrend />
          <ExpenseSummary />
        </div>
      )}
      defaults={{ category: "Fuel", category_manual: "", amount: 0, description: "", vehicle_no: "", office: "Chennai", date: today() }}
      onAiCategorize={aiCategorize}
      fields={[
        { key: "description", label: "Description", type: "textarea", placeholder: "Diesel refill at Nashik pump" },
        { key: "category", label: "Category", type: "select", options: CATEGORIES, ai: true },
        { key: "category_manual", label: "Category (manual entry)", type: "text", placeholder: "Type other expense e.g. Toll, Tea", suggestions: manualSuggestions },
        { key: "amount", label: "Amount (₹)", type: "number", placeholder: "8500" },
        { key: "office", label: "Branch", type: "select", options: BRANCHES },
        { key: "vehicle_no", label: "Vehicle no. (optional)", type: "text", placeholder: "MH12 AB 1234" },
        { key: "date", label: "Date", type: "date" },
      ]}
      filters={[
        { key: "office", label: "Branch", type: "select", options: ["All", ...BRANCHES], match: (r, v) => !v || v === "All" || (r.office || "Chennai") === v },
        { key: "category", label: "Category", type: "select", options: ["All", ...CATEGORIES], match: (r, v) => !v || v === "All" || (r.category || "Other") === v },
        { key: "date_from", label: "Date From", type: "date", match: (r, v) => !v || (r.date || "") >= v },
        { key: "date_to", label: "To", type: "date", match: (r, v) => !v || (r.date || "") <= v },
      ]}
      columns={[
        { key: "office", label: "Branch", render: (r) => r.office || "Chennai" },
        { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
        { key: "category", label: "Category" },
        { key: "description", label: "Description" },
        { key: "vehicle_no", label: "Vehicle" },
        {
          key: "proof",
          label: "Bill Proof",
          render: (r) =>
            r.proof_path ? (
              <a
                href={proofUrl(r)}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 hover:underline"
                title={r.proof_filename || "View bill photo"}
                data-testid="view-proof-link"
              >
                <Eye className="h-3.5 w-3.5" /> View
              </a>
            ) : Number(r.amount) >= proofLimit ? (
              <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800" title={`Amount ≥ ₹${proofLimit.toLocaleString("en-IN")} — attach bill proof`} data-testid="expense-proof-missing-badge">
                Proof missing
              </span>
            ) : (
              "—"
            ),
        },
        { key: "amount", label: "Amount", align: "right", render: (r) => inr(r.amount) },
      ]}
      rowActions={(r) => (
        <>
          <ProofActions row={r} />
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-emerald-600"
            title="Send expense to admin on WhatsApp"
            onClick={() => expenseWhatsApp(r)}
            data-testid="whatsapp-expense-btn"
          >
            <MessageCircle className="h-4 w-4" />
          </Button>
        </>
      )}
    />
  );
}
